// src/features/services/hooks/useServices.ts
import { useEffect, useState } from 'react';

import { fetchServices } from '../api/services.api';
import type { ServiceDTO } from '../api/services.api';
import type { Service, ServiceStatus } from '../components/ServiceRow';

function normalizeStatus(status: unknown): ServiceStatus {
  if (status === 'active' || status === 'expiring' || status === 'expired') {
    return status;
  }

  // Backend hasn't confirmed a status field yet — default to 'active'
  // rather than inventing a derived value (e.g. from expiry date).
  return 'active';
}

function mapService(dto: ServiceDTO): Service {
  return {
    id: String(dto.id),
    name: dto.name ?? dto.plan_name ?? '-',
    customerName: dto.customer_name ?? '-',
    status: normalizeStatus(dto.status),
    expiresAt: dto.expires_at ?? '-',
  };
}

export interface UseServicesResult {
  services: Service[];
  loading: boolean;
  error: string | null;
}

/**
 * Data-access hook for the Services feature. Fetches GET /api/services
 * through apiClient and maps the response onto the existing `Service`
 * shape used by ServiceList/ServiceRow/ServiceWorkspace.
 */
export function useServices(): UseServicesResult {
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);

      try {
        const response = await fetchServices();

        if (!cancelled) {
          setServices(response.services.map(mapService));
        }
      } catch {
        if (!cancelled) {
          setServices([]);
          setError('خطا در دریافت فهرست سرویس‌ها.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, []);

  return { services, loading, error };
}
